import { useMutation } from '@apollo/client';
import { Badge } from 'components/Badge/Badge';
import { Button } from 'components/Button/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/Card/Card';
import { Label } from 'components/Label/Label';
import { Select } from 'components/Select/Select';
import { useToast } from 'components/Toast/use-toast';
import { Typography } from 'components/Typograhpy/Typography';
import { cn } from 'lib/utils';
import {
    AlertCircle,
    ArrowLeft,
    ArrowRight,
    Check,
    CheckCircle,
    FileSpreadsheet,
    Loader2,
    Upload,
    XCircle,
} from 'lucide-react';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import {
    IMPORT_UPLOAD,
    IMPORT_CONFIGURE,
    IMPORT_PREVIEW,
    IMPORT_START,
} from 'services/import';

type Step = 'upload' | 'mapping' | 'preview' | 'processing' | 'complete';

const getStepClass = (currentStep: Step, targetStep: Step): string => {
    const steps: Step[] = ['upload', 'mapping', 'preview', 'complete'];
    const isActive = currentStep === targetStep || (currentStep === 'processing' && targetStep === 'complete');
    if (isActive) return 'bg-blue-600 text-white';
    
    const currentIndex = currentStep === 'processing' ? steps.indexOf('complete') : steps.indexOf(currentStep);
    const targetIndex = steps.indexOf(targetStep);
    const isPast = targetIndex < currentIndex;
    if (isPast) return 'bg-green-600 text-white';
    
    return 'bg-zinc-200 text-zinc-600';
};

const isStepCompleted = (currentStep: Step, targetStep: Step): boolean => {
    const steps: Step[] = ['upload', 'mapping', 'preview', 'complete'];
    const currentIndex = currentStep === 'processing' ? steps.indexOf('complete') : steps.indexOf(currentStep);
    const targetIndex = steps.indexOf(targetStep);
    return targetIndex < currentIndex;
};

const getDuplicateStrategyLabel = (strategy: string): string => {
    const labels: Record<string, string> = {
        SKIP: 'Skip duplicates',
        UPDATE: 'Update existing members',
        CREATE_NEW: 'Create as new',
    };
    return labels[strategy] || 'Skip duplicates';
};

interface ImportJob {
    id: string;
    status: string;
    originalFilename: string;
    fileType: string;
    totalRows: number;
    processedRows: number;
    successCount: number;
    errorCount: number;
    skipCount: number;
    progressPercent: number;
    detectedColumns: string[];
    columnMapping: Record<string, string>;
    mappableFields: Record<string, { required: boolean; type: string }>;
    errorSummary: string[];
}

interface ImportRow {
    id: string;
    rowNumber: number;
    rawData: Record<string, any>;
    mappedData: Record<string, any>;
    status: string;
    errors: string[];
}

export const ImportWizard = () => {
    const [step, setStep] = useState<Step>('upload');
    const [job, setJob] = useState<ImportJob | null>(null);
    const [mapping, setMapping] = useState<Record<string, string>>({});
    const [duplicateStrategy, setDuplicateStrategy] = useState('SKIP');
    const [previewRows, setPreviewRows] = useState<ImportRow[]>([]);
    const [validCount, setValidCount] = useState(0);
    const [invalidCount, setInvalidCount] = useState(0);
    const { toast } = useToast();

    const [uploadFile, { loading: uploading }] = useMutation(IMPORT_UPLOAD);
    const [configureImport, { loading: configuring }] = useMutation(IMPORT_CONFIGURE);
    const [previewImport, { loading: previewing }] = useMutation(IMPORT_PREVIEW);
    const [startImport] = useMutation(IMPORT_START);

    const onDrop = useCallback(
        async (acceptedFiles: File[]) => {
            if (acceptedFiles.length === 0) return;

            const file = acceptedFiles[0];
            try {
                const { data } = await uploadFile({ variables: { file } });
                if (data?.importUpload?.job) {
                    setJob(data.importUpload.job);
                    setMapping(data.importUpload.suggestedMapping || {});
                    setStep('mapping');
                    toast({ description: 'File uploaded successfully' });
                }
            } catch (error: any) {
                toast({
                    description: error.message || 'Failed to upload file',
                    variant: 'destructive',
                });
            }
        },
        [uploadFile, toast]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: {
            'text/csv': ['.csv'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/vnd.ms-excel': ['.xls'],
        },
        maxFiles: 1,
    });

    const handleConfigure = async () => {
        if (!job) return;

        try {
            const { data } = await configureImport({
                variables: {
                    jobId: job.id,
                    columnMapping: JSON.stringify(mapping),
                    duplicateStrategy,
                    duplicateCheckField: 'email',
                },
            });

            if (data?.importConfigure) {
                setValidCount(data.importConfigure.validCount);
                setInvalidCount(data.importConfigure.invalidCount);

                // Get preview
                const { data: previewData } = await previewImport({
                    variables: { jobId: job.id, limit: 50 },
                });

                if (previewData?.importPreview?.rows) {
                    setPreviewRows(previewData.importPreview.rows);
                }

                setStep('preview');
            }
        } catch (error: any) {
            toast({
                description: error.message || 'Failed to configure import',
                variant: 'destructive',
            });
        }
    };

    const handleStartImport = async () => {
        if (!job) return;

        setStep('processing');

        try {
            const { data } = await startImport({ variables: { jobId: job.id } });
            if (data?.importStart?.job) {
                setJob(data.importStart.job);
                setStep('complete');
            }
        } catch (error: any) {
            toast({
                description: error.message || 'Failed to start import',
                variant: 'destructive',
            });
            setStep('preview');
        }
    };

    const resetWizard = () => {
        setStep('upload');
        setJob(null);
        setMapping({});
        setPreviewRows([]);
        setValidCount(0);
        setInvalidCount(0);
    };

    const updateMapping = (sourceColumn: string, targetField: string) => {
        setMapping(prev => {
            if (!targetField) {
                const { [sourceColumn]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [sourceColumn]: targetField };
        });
    };

    const mappableFieldOptions = job?.mappableFields
        ? Object.entries(job.mappableFields).map(([field, config]) => ({
              value: field,
              label: `${field}${config.required ? ' *' : ''}`,
          }))
        : [];

    return (
        <div className="max-w-4xl mx-auto p-6">
            <div className="mb-8">
                <Typography.H2>Import Members</Typography.H2>
                <p className="text-zinc-500 mt-2">
                    Upload a CSV or Excel file to import member data.
                </p>
            </div>

            {/* Progress Steps */}
            <div className="mb-8">
                <div className="flex items-center justify-between">
                    {(['upload', 'mapping', 'preview', 'complete'] as Step[]).map((s, i) => (
                        <div key={s} className="flex items-center">
                            <div
                                className={cn(
                                    'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                                    getStepClass(step, s)
                                )}
                            >
                                {isStepCompleted(step, s) ? (
                                    <Check className="w-4 h-4" />
                                ) : (
                                    i + 1
                                )}
                            </div>
                            {i < 3 && (
                                <div
                                    className={cn(
                                        'w-24 h-1 mx-2',
                                        isStepCompleted(step, s) ? 'bg-green-600' : 'bg-zinc-200'
                                    )}
                                />
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex justify-between mt-2 text-sm text-zinc-500">
                    <span>Upload</span>
                    <span>Map Columns</span>
                    <span>Preview</span>
                    <span>Complete</span>
                </div>
            </div>

            {/* Step 1: Upload */}
            {step === 'upload' && (
                <Card>
                    <CardHeader>
                        <CardTitle>Upload File</CardTitle>
                        <CardDescription>
                            Select a CSV or Excel file containing member data
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div
                            {...getRootProps()}
                            className={cn(
                                'border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors',
                                isDragActive ? 'border-blue-500 bg-blue-50' : 'border-zinc-300 hover:border-zinc-400'
                            )}
                        >
                            <input {...getInputProps()} />
                            {uploading ? (
                                <Loader2 className="w-12 h-12 mx-auto text-zinc-400 animate-spin" />
                            ) : (
                                <Upload className="w-12 h-12 mx-auto text-zinc-400" />
                            )}
                            <p className="mt-4 text-lg font-medium">
                                {isDragActive ? 'Drop the file here' : 'Drag & drop a file here'}
                            </p>
                            <p className="mt-2 text-sm text-zinc-500">
                                or click to browse (CSV, XLS, XLSX)
                            </p>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Step 2: Column Mapping */}
            {step === 'mapping' && job && (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <FileSpreadsheet className="w-5 h-5" />
                            {job.originalFilename}
                        </CardTitle>
                        <CardDescription>
                            {job.totalRows} rows detected. Map columns to member fields.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="grid gap-4">
                            {job.detectedColumns.map(column => (
                                <div key={column} className="flex items-center gap-4">
                                    <div className="w-1/3">
                                        <Label className="text-zinc-600">{column}</Label>
                                    </div>
                                    <ArrowRight className="w-4 h-4 text-zinc-400" />
                                    <div className="w-1/2">
                                        <Select
                                            value={mappableFieldOptions.find(opt => opt.value === mapping[column]) || null}
                                            onChange={opt => updateMapping(column, opt?.value || '')}
                                            options={[{ value: '', label: '-- Skip this column --' }, ...mappableFieldOptions]}
                                            placeholder="Select field..."
                                            isClearable
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        <div className="border-t pt-4">
                            <Label>Duplicate Handling</Label>
                            <div className="mt-2">
                                <Select
                                    value={{ value: duplicateStrategy, label: getDuplicateStrategyLabel(duplicateStrategy) }}
                                    onChange={opt => setDuplicateStrategy(opt?.value || 'SKIP')}
                                    options={[
                                        { value: 'SKIP', label: 'Skip duplicates' },
                                        { value: 'UPDATE', label: 'Update existing members' },
                                        { value: 'CREATE_NEW', label: 'Create as new' },
                                    ]}
                                />
                            </div>
                        </div>

                        <div className="flex justify-between pt-4">
                            <Button variant="outline" onClick={resetWizard}>
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back
                            </Button>
                            <Button onClick={handleConfigure} disabled={configuring || previewing}>
                                {(configuring || previewing) && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                Continue
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Step 3: Preview */}
            {step === 'preview' && job && (
                <Card>
                    <CardHeader>
                        <CardTitle>Preview Import</CardTitle>
                        <CardDescription>
                            Review the data before importing. {validCount} valid, {invalidCount} invalid.
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex gap-4 mb-4">
                            <Badge variant="default" className="bg-green-100 text-green-700">
                                <CheckCircle className="w-4 h-4 mr-1" />
                                {validCount} Valid
                            </Badge>
                            {invalidCount > 0 && (
                                <Badge variant="destructive">
                                    <XCircle className="w-4 h-4 mr-1" />
                                    {invalidCount} Invalid
                                </Badge>
                            )}
                        </div>

                        <div className="border rounded-lg overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-zinc-50">
                                    <tr>
                                        <th className="px-4 py-2 text-left">#</th>
                                        <th className="px-4 py-2 text-left">Status</th>
                                        <th className="px-4 py-2 text-left">Email</th>
                                        <th className="px-4 py-2 text-left">Name</th>
                                        <th className="px-4 py-2 text-left">Errors</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewRows.slice(0, 20).map(row => (
                                        <tr key={row.id} className="border-t">
                                            <td className="px-4 py-2">{row.rowNumber}</td>
                                            <td className="px-4 py-2">
                                                {row.status === 'VALID' ? (
                                                    <CheckCircle className="w-4 h-4 text-green-600" />
                                                ) : (
                                                    <AlertCircle className="w-4 h-4 text-red-600" />
                                                )}
                                            </td>
                                            <td className="px-4 py-2">{row.mappedData?.email || '-'}</td>
                                            <td className="px-4 py-2">
                                                {row.mappedData?.first_name} {row.mappedData?.last_name}
                                            </td>
                                            <td className="px-4 py-2 text-red-600 text-xs">
                                                {row.errors?.join(', ')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex justify-between pt-4">
                            <Button variant="outline" onClick={() => setStep('mapping')}>
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Back
                            </Button>
                            <Button onClick={handleStartImport} disabled={validCount === 0}>
                                Start Import ({validCount} members)
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Step 4: Processing */}
            {step === 'processing' && (
                <Card>
                    <CardContent className="py-12 text-center">
                        <Loader2 className="w-12 h-12 mx-auto text-blue-600 animate-spin" />
                        <p className="mt-4 text-lg font-medium">Processing Import...</p>
                        <p className="mt-2 text-zinc-500">Please wait while we import your data.</p>
                    </CardContent>
                </Card>
            )}

            {/* Step 5: Complete */}
            {step === 'complete' && job && (
                <Card>
                    <CardContent className="py-12 text-center">
                        <CheckCircle className="w-16 h-16 mx-auto text-green-600" />
                        <h3 className="mt-4 text-xl font-semibold">Import Complete!</h3>
                        <div className="mt-6 flex justify-center gap-8">
                            <div className="text-center">
                                <div className="text-3xl font-bold text-green-600">{job.successCount}</div>
                                <div className="text-sm text-zinc-500">Imported</div>
                            </div>
                            {job.skipCount > 0 && (
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-amber-600">{job.skipCount}</div>
                                    <div className="text-sm text-zinc-500">Skipped</div>
                                </div>
                            )}
                            {job.errorCount > 0 && (
                                <div className="text-center">
                                    <div className="text-3xl font-bold text-red-600">{job.errorCount}</div>
                                    <div className="text-sm text-zinc-500">Errors</div>
                                </div>
                            )}
                        </div>

                        {job.errorSummary && job.errorSummary.length > 0 && (
                            <div className="mt-6 text-left max-w-md mx-auto">
                                <p className="text-sm font-medium text-red-600 mb-2">Errors:</p>
                                <div className="bg-red-50 rounded p-3 text-sm text-red-700 max-h-32 overflow-y-auto">
                                    {job.errorSummary.slice(0, 5).map((err, i) => (
                                        <div key={i}>{err}</div>
                                    ))}
                                    {job.errorSummary.length > 5 && (
                                        <div className="mt-1 text-red-500">
                                            ...and {job.errorSummary.length - 5} more
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="mt-8">
                            <Button onClick={resetWizard}>Import Another File</Button>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};
